import React, { useCallback, useEffect, useState } from "react";
import { equals, find, lensProp, map, propEq, set } from "ramda";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import { isBlank, notBlank } from "../util/ext";
import { Skeleton } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import {
  useCountries,
  useRegionCreate,
  useRegions,
  useStates,
} from "./regionService";
import { useAuthenticationCreate } from "./authenticationService";
import OrganizationAutoComplete from "../user-admin/OrganizationAutoComplete";
import Typography from "@mui/material/Typography";
import { DateTime } from "luxon";

const intitialRegion = {
  city: "",
  county: "",
  state: null,
  country: null,
  expiresDate: null,
};

const AddRegionAuthenticationDialog = ({
  open,
  organizationId,
  authentication,
  onClose,
  onSave,
}) => {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(
    organizationId,
  );
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [newRegion, setNewRegion] = useState(intitialRegion);

  const regions = useRegions();
  const [regionList, setRegionList] = useState([]);
  const countries = useCountries();
  const states = useStates(newRegion.country);
  const [stateList, setStateList] = useState([]);

  const [showSubRegions, setShowSubRegions] = useState(false);
  useEffect(() => {
    if (notBlank(newRegion.country)) {
      setShowSubRegions(true);
    } else {
      setShowSubRegions(false);
    }
    setNewRegion(prev => set(lensProp("state"), null, prev));
    setSelectedRegion(null);
  }, [newRegion.country]);

  useEffect(() => {
    if (regions.isSuccess && notBlank(regions.data)) {
      const list = map(region => {
        let label = region.country || "";
        label = region.state
          ? `${region.state}${label ? ", " + label : ""}`
          : label;
        label = region.county ? `${region.county}, ${label}` : label;
        label = region.city ? ` ${region.city}, ${label}` : label;

        return {
          regionId: region.regionId,
          label,
        };
      }, regions.data);
      setRegionList(list);

      if (notBlank(authentication) && authentication.regionId > 0) {
        setSelectedRegion(
          find(propEq("regionId", authentication.regionId), list),
        );
      } else {
        setSelectedRegion(null);
      }
    } else {
      setRegionList([]);
    }
  }, [authentication, regions.isSuccess, regions.data]);

  useEffect(() => {
    if (states.isSuccess && notBlank(states.data)) {
      const list = map(
        state => ({
          code: state.code.slice(-2),
          name: `${state.name} (${state.code.slice(-2)})`,
        }),
        states.data,
      );
      setStateList(list);
    } else {
      setStateList([]);
    }
  }, [states.isSuccess, states.data]);

  const onFieldChanged = field => (event, value) => {
    console.log(value);
    const val = notBlank(value)
      ? value
      : notBlank(event)
      ? event.target.value
      : event;
    console.log(val);
    const update = set(lensProp(field), val, newRegion);
    setNewRegion(update);
  };

  const onDateChanged = field => value => {
    const val = value.toFormat("MM/dd/yyyy");
    const update = set(lensProp(field), val, newRegion);
    setNewRegion(update);
  };

  const handleOrganizationChange = orgId => {
    setSelectedOrganizationId(orgId);
  };

  const handleRegionChange = useCallback((event, value) => {
    setSelectedRegion(value);
    setShowSubRegions(false);
    setNewRegion(intitialRegion);
  }, []);

  const regionCreate = useRegionCreate();
  const createNewRegion = useCallback(async () => {
    const regionBody = {
      country: notBlank(newRegion.country) ? newRegion.country.code : null,
      state: notBlank(newRegion.state) ? newRegion.state.code : null,
      county: newRegion.county,
      city: newRegion.city,
    };

    return await regionCreate.mutateAsync({ data: regionBody });
  }, [newRegion, regionCreate]);

  const authenticationCreate = useAuthenticationCreate();
  const handleApply = useCallback(async () => {
    if (notBlank(selectedOrganizationId)) {
      try {
        const region = notBlank(selectedRegion)
          ? selectedRegion
          : await createNewRegion();

        const createBody = {
          regionId: region.regionId,
          dateExpires: newRegion.expiresDate,
        };

        await authenticationCreate.mutateAsync({
          resourceIds: {
            organizationId: selectedOrganizationId,
            type: "region",
          },
          data: createBody,
        });
        onSave(createBody);
      } catch (error) {
        console.log(error);
        // setErrorMessage(error);
      }
    } else {
      throw new Error("Both Organization and Region are required.");
    }
  }, [
    selectedOrganizationId,
    selectedRegion,
    newRegion,
    createNewRegion,
    authenticationCreate,
    onSave,
  ]);
  const handleCancel = useCallback(() => onClose(), [onClose]);

  return (
    <Dialog open={open} aria-labelledby="region-dialog-title">
      <DialogTitle id="region-dialog-title">
        Add Geolocation Authentication
      </DialogTitle>
      <DialogContent>
        <Box mb={5}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <OrganizationAutoComplete
                autoFocus
                value={selectedOrganizationId}
                label="Organization"
                onChange={handleOrganizationChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <DatePicker
                  format="MM/dd/yyyy"
                  label="Date Expires (optional)"
                  value={
                    newRegion?.expiresDate
                      ? DateTime.fromFormat(newRegion.expiresDate, "MM/dd/yyyy")
                      : null
                  }
                  onChange={onDateChanged("expiresDate")}
                  slotProps={{
                    textField: {
                      id: "date-expires",
                    },
                    openPickerButton: {
                      "aria-label": "change date",
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography variant="h3">Use an existing Region</Typography>
        </Box>

        {isBlank(regions) ? (
          <Skeleton variant="text" />
        ) : (
          <Autocomplete
            options={regionList}
            value={selectedRegion || ""}
            getOptionLabel={region => {
              return notBlank(region) ? region.label : "";
            }}
            isOptionEqualToValue={(opt, val) => {
              return (
                isBlank(val) ||
                val.regionId < 0 ||
                equals(opt.regionId, val.regionId)
              );
            }}
            onChange={handleRegionChange}
            renderInput={params => {
              params.inputProps["aria-autocomplete"] = "none";
              params.inputProps["id"] = null;
              return (
                <TextField
                  {...params}
                  type="text"
                  name={"existing-region-" + Math.random()}
                  variant="standard"
                  label="Search for a Region"
                  fullWidth
                />
              );
            }}
          />
        )}

        <Box mt={5} mb={5}>
          <Typography variant="h6">- or -</Typography>
        </Box>

        <Box mt={5}>
          <Typography variant="h3">Create a new Region</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {countries.isLoading || isBlank(countries.data) ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                options={countries.data}
                value={newRegion.country || ""}
                getOptionLabel={country => {
                  return notBlank(country) ? country.name : "";
                }}
                isOptionEqualToValue={(opt, val) => {
                  return isBlank(val) || equals(opt.code, val.code);
                }}
                onChange={onFieldChanged("country")}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      name={"country-" + Math.random()}
                      variant="standard"
                      label="Select a Country"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              autoSelect
              disabled={!showSubRegions}
              options={stateList}
              value={newRegion.state || ""}
              getOptionLabel={state => {
                return notBlank(state) ? state.name : "";
              }}
              isOptionEqualToValue={(opt, val) => {
                return isBlank(val) || equals(opt.code, val.code);
              }}
              onChange={onFieldChanged("state")}
              renderInput={params => {
                params.inputProps["aria-autocomplete"] = "none";
                params.inputProps["id"] = null;
                return (
                  <TextField
                    {...params}
                    type="text"
                    name={"country-" + Math.random()}
                    variant="standard"
                    label="Select a State"
                    fullWidth
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              type="text"
              margin="dense"
              id="county"
              label="County"
              disabled={!showSubRegions}
              value={newRegion.county}
              onChange={onFieldChanged("county")}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              type="text"
              margin="dense"
              id="city"
              label="City"
              disabled={!showSubRegions}
              value={newRegion.city}
              onChange={onFieldChanged("city")}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRegionAuthenticationDialog;
